import React from 'react';
import { Sparkles, type LucideProps } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AIIconProps extends LucideProps {
  variant?: 'default' | 'primary' | 'muted';
}

/**
 * Standardized AI Icon component for the platform
 * 
 * Usage:
 * - Default: Standard AI features (subtle gradient)
 * - Primary: Active/prominent AI actions (purple gradient)
 * - Muted: Disabled or secondary AI features
 * 
 * @example
 * <AIIcon />
 * <AIIcon variant="primary" className="w-5 h-5" />
 * <AIIcon variant="muted" />
 */
export function AIIcon({ variant = 'default', className, ...props }: AIIconProps) {
  const gradientId = React.useId();
  
  const gradients = {
    default: {
      from: '#fe68bf', // accent-400 (lighter pink)
      to: '#e61885'    // accent-600 (deeper pink)
    },
    primary: {
      from: '#f437a8', // accent-500 (main accent)
      to: '#c8096a'    // accent-700 (darker magenta)
    },
    muted: {
      from: '#9ca3af', // gray-400
      to: '#6b7280'    // gray-500
    }
  };

  const gradient = gradients[variant];

  return (
    <div className={cn("relative inline-flex", className)}>
      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={gradient.from} />
            <stop offset="100%" stopColor={gradient.to} />
          </linearGradient>
        </defs>
      </svg>
      <Sparkles 
        {...props}
        className={cn(
          "transition-all duration-200",
          variant === 'default' && "hover:scale-110",
          variant === 'primary' && "hover:scale-110 hover:rotate-12",
          className
        )}
        style={{ 
          stroke: `url(#${gradientId})`,
          fill: variant === 'primary' ? `url(#${gradientId})` : 'none',
          strokeWidth: variant === 'primary' ? 1.5 : 2
        }}
      />
    </div>
  );
}
